import { useMemo } from "react";

import { DefaultButton, PrimaryButton } from "@fluentui/react/lib/Button";
import { Stack } from "@fluentui/react/lib/Stack";

import { WithConfiguration } from "Components/Generic/Configuration";
import { Merge } from "Components/Generic/Merge";
import { IMergeEntryProps } from "Components/Generic/Merge/MergeEntry";
import { WithTheme } from "Components/Generic/Theme/WithTheme";
import { WithTranslator } from "Components/Generic/Translator";
import { getItemType } from "Utils/Item";
import { useProjectItem } from "../../Hooks/UseProjectItem";
import { WithProjectItemContext } from "../../ProjectItemContext/WithProjectItemContext";
import { useResult } from "../MergeContext/UseResult";
import { WithMergeContext } from "../MergeContext/WithMergeContext";
import { IMergeContentProps } from "./IMergeContentProps";
import { useMergeContentStyles } from "./UseMergeContentStyles";

type CustomIMergeEntryProps = Omit<IMergeEntryProps, "onChange" | "translator" | "index" | "config" | "isConfigReady">;

export const MergeContent = WithTheme(
  WithProjectItemContext(
    WithMergeContext(
      WithTranslator(
        WithConfiguration((props: IMergeContentProps): JSX.Element => {
          const {
            config,
            mergeSelectionMap,
            onMergeChange,
            onPrevious,
            onSelectAll,
            onValidate,
            projectId,
            projectItemId,
            selectedResultItemId,
            theme: { spacing },
            translator
          } = props;

          const { projectItem } = useProjectItem(projectId, projectItemId);
          const result = useResult(selectedResultItemId);
          const { styles, mergeStyles } = useMergeContentStyles();
          const columnList = config.getDCMergeFields(getItemType(projectItem));

          const entries = useMemo(
            () => columnList.map<CustomIMergeEntryProps>(({ fieldName }) => {
              const sourceValue = projectItem?.[fieldName];
              const targetValue = result?.[fieldName];
              const mergePreference = config.getDCMergePreference();
              const currentSelection = mergeSelectionMap.get(fieldName);

              const isPreselectedTarget = currentSelection == null
                  && mergePreference === `source`
                  && sourceValue == null
                  && targetValue != null;

              const isPreselectedSource = currentSelection == null
                  && mergePreference === `target`
                  && sourceValue != null
                  && targetValue == null;

              if (isPreselectedTarget) {
                onMergeChange(`target.${fieldName}`, targetValue);
              } else if (isPreselectedSource) {
                onMergeChange(`source.${fieldName}`, sourceValue);
              }

              return {
                fieldName,
                entityType: getItemType(projectItem),
                sourceItem: projectItem,
                targetItem: result,
                selectedKey: currentSelection ?? mergePreference,
                allowDisableValue: false
              };
            }),
            [columnList, config, mergeSelectionMap, onMergeChange, projectItem, result]
          );

          return (
            <Stack tokens={{ childrenGap: spacing.m }} className={styles.root}>
              <Stack.Item className={styles.grid}>
                <Merge
                  alternativeSourceToken="dc.merge.current"
                  alternativeTargetToken="dc.merge.duplicate"
                  entries={entries}
                  onChange={onMergeChange}
                  onSelectAll={onSelectAll}
                  styleSet={mergeStyles}
                />
              </Stack.Item>
              <Stack horizontal disableShrink horizontalAlign="end" tokens={{ childrenGap: spacing.s1 }}>
                <DefaultButton onClick={onPrevious} iconProps={{ iconName: `ChevronLeft` }}>
                  {translator.get(`generic.previous`, `Previous`)}
                </DefaultButton>
                <PrimaryButton onClick={onValidate}>{translator.get(`generic.next`, `Next`)}</PrimaryButton>
              </Stack>
            </Stack>
          );
        })
      )
    )
  )
);
